import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { getActiveLearner } from "@/lib/learner";
import Logo from "@/components/Logo";
import CertificatePreview from "@/components/CertificatePreview";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Award, Lock, CheckCircle2, Circle, Download,
  Linkedin, Share2, Pencil, Sparkles, ArrowRight,
} from "lucide-react";

export default function CertificatePage() {
  const learner = getActiveLearner();
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editingName, setEditingName] = useState(false);

  const cert = elig?.certificate || null;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/program-certificate/eligibility", {
        params: { learner_name: learner?.name || "" },
      });
      setElig(data);
      if (!data.certificate) setFullName(learner?.name || "");
    } catch {
      toast.error("Couldn't load your certificate status. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [learner?.name]);

  useEffect(() => { load(); }, [load]);

  const generate = async (e) => {
    e?.preventDefault();
    const name = fullName.trim();
    if (name.length < 2) { toast.error("Please enter your full name."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/program-certificate/generate", {
        learner_name: learner?.name || "",
        full_name: name,
      });
      setElig((prev) => ({ ...prev, certificate: data }));
      setEditingName(false);
      toast.success("Your certificate is ready!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate the certificate.");
    } finally {
      setGenerating(false);
    }
  };

  const verifyUrl = cert ? `${window.location.origin}/certificate/${cert.cert_id}` : "";

  const addToLinkedIn = () => {
    const issued = new Date(cert.issued_at);
    const url =
      "https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME" +
      `&name=${encodeURIComponent(cert.program_title)}` +
      `&organizationName=${encodeURIComponent(cert.institution)}` +
      `&issueYear=${issued.getFullYear()}&issueMonth=${issued.getMonth() + 1}` +
      `&certUrl=${encodeURIComponent(verifyUrl)}` +
      `&certId=${encodeURIComponent(cert.cert_id)}`;
    window.open(url, "_blank", "noopener");
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`,
      "_blank", "noopener",
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo className="h-10" />
          <Link to="/learn" data-testid="certificate-back-link" className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to Academy
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E53935] font-semibold mb-2">Corporate L&amp;D · UniVicoustic Learning Academy</div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-[#222222]">Program Certificate</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Pass every module quiz to earn your certificate for <strong>A Comprehensive and Advanced Acoustics Training Program</strong>.
          </p>
        </div>

        {loading ? (
          <Card className="p-12 text-center border-slate-200" data-testid="certificate-loading">
            <div className="w-8 h-8 mx-auto border-2 border-[#E53935] border-t-transparent rounded-full animate-spin" />
            <div className="mt-3 text-sm text-slate-500">Checking your progress…</div>
          </Card>
        ) : cert ? (
          <CertEarned
            cert={cert}
            editingName={editingName}
            setEditingName={setEditingName}
            fullName={fullName}
            setFullName={setFullName}
            generate={generate}
            generating={generating}
            addToLinkedIn={addToLinkedIn}
            shareOnLinkedIn={shareOnLinkedIn}
          />
        ) : elig?.eligible ? (
          <NameForm fullName={fullName} setFullName={setFullName} generate={generate} generating={generating} granted={elig?.granted} />
        ) : (
          <LockedView elig={elig} />
        )}
      </main>
    </div>
  );
}

const LockedView = ({ elig }) => {
  const pct = elig?.total_quizzes ? Math.round((elig.passed_quizzes / elig.total_quizzes) * 100) : 0;
  return (
    <div data-testid="certificate-locked-view">
      <Card className="p-6 md:p-8 border-slate-200 mb-8 bg-gradient-to-r from-[#222222] to-[#3a1212] text-white">
        <div className="flex items-center gap-5">
          <div className="shrink-0 w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
            <Lock size={24} className="text-[#E53935]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-1">Certificate locked</div>
            <div className="text-lg font-semibold">Pass all module quizzes to unlock your certificate</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 h-2 bg-white/15 rounded-full overflow-hidden max-w-md">
                <div className="h-full bg-gradient-to-r from-[#E53935] to-[#ff8a78] transition-all" style={{ width: `${Math.max(2, pct)}%` }} />
              </div>
              <div className="text-xs text-white/80 font-mono shrink-0" data-testid="certificate-progress-count">
                {elig?.passed_quizzes ?? 0}/{elig?.total_quizzes ?? 0} quizzes passed
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        {(elig?.courses || []).map((c, i) => (
          <Card key={c.course_id} className={`p-5 border ${c.all_passed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`certificate-course-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                {c.all_passed
                  ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                  : <Circle size={18} className="text-slate-300 shrink-0" />}
                <h3 className="font-display font-semibold text-[#222222] truncate">{c.course_title}</h3>
              </div>
              {!c.all_passed && (
                <Link to={`/learn/courses/${c.course_id}`} className="text-xs text-[#E53935] font-semibold inline-flex items-center gap-1 shrink-0 hover:underline">
                  Continue <ArrowRight size={11} />
                </Link>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {c.modules.map((m) => (
                <div key={m.module_id} className="flex items-center justify-between text-sm py-0.5">
                  <div className="flex items-center gap-2 min-w-0">
                    {m.passed
                      ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                      : <Circle size={14} className="text-slate-300 shrink-0" />}
                    <span className={`truncate ${m.passed ? "text-slate-600" : "text-slate-500"}`}>{m.title}</span>
                  </div>
                  <span className={`shrink-0 text-xs font-mono ml-2 ${m.passed ? "text-emerald-700" : "text-slate-400"}`}>
                    {m.best_percentage != null ? `${Math.round(m.best_percentage)}%` : "—"}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const NameForm = ({ fullName, setFullName, generate, generating, granted }) => (
  <Card className="p-8 md:p-10 border-[#E53935]/30 bg-gradient-to-br from-white via-[#fff5f5] to-white text-center" data-testid="certificate-name-form">
    <div className="w-16 h-16 mx-auto rounded-full bg-[#E53935] flex items-center justify-center mb-4">
      <Award size={30} className="text-white" />
    </div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-2 flex items-center justify-center gap-2">
      <Sparkles size={12} /> {granted ? "Course creator access" : "Congratulations — all quizzes passed"}
    </div>
    <h2 className="text-2xl font-display font-bold text-[#222222] mb-2">Claim your certificate</h2>
    <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
      Enter your full name exactly as you'd like it printed on the certificate.
    </p>
    <form onSubmit={generate} className="max-w-sm mx-auto">
      <input
        data-testid="certificate-name-input"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        placeholder="Your full name"
        maxLength={90}
        className="w-full px-4 py-3 border border-slate-300 rounded-md text-center text-lg font-medium focus:outline-none focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935]"
      />
      <button
        type="submit"
        data-testid="certificate-generate-button"
        disabled={generating || fullName.trim().length < 2}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#222222] hover:bg-black disabled:opacity-50 text-white font-medium rounded-md transition-colors"
      >
        {generating ? "Generating…" : <>Generate certificate <Award size={16} /></>}
      </button>
    </form>
  </Card>
);

const CertEarned = ({ cert, editingName, setEditingName, fullName, setFullName, generate, generating, addToLinkedIn, shareOnLinkedIn }) => (
  <div data-testid="certificate-earned-view">
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-xs font-semibold">
        <CheckCircle2 size={13} /> Certificate earned · ID {cert.cert_id}
      </div>
      {!editingName && (
        <button
          data-testid="certificate-edit-name"
          onClick={() => { setFullName(cert.full_name); setEditingName(true); }}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#E53935] transition-colors"
        >
          <Pencil size={12} /> Edit name
        </button>
      )}
    </div>

    {editingName && (
      <form onSubmit={generate} className="mb-6 flex gap-2 max-w-md" data-testid="certificate-rename-form">
        <input
          data-testid="certificate-rename-input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          maxLength={90}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#E53935]"
        />
        <button type="submit" disabled={generating} className="px-4 py-2 bg-[#222222] text-white text-sm rounded-md hover:bg-black disabled:opacity-50">
          {generating ? "Saving…" : "Update"}
        </button>
        <button type="button" onClick={() => setEditingName(false)} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800">Cancel</button>
      </form>
    )}

    <CertificatePreview cert={cert} />

    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
      <a
        href={`${API}/program-certificate/${cert.cert_id}/pdf`}
        data-testid="certificate-download-pdf"
        className="inline-flex items-center gap-2 px-5 py-3 bg-[#222222] hover:bg-black text-white font-medium rounded-md transition-colors"
      >
        <Download size={16} /> Download PDF
      </a>
      <button
        data-testid="certificate-add-linkedin"
        onClick={addToLinkedIn}
        className="inline-flex items-center gap-2 px-5 py-3 bg-[#0A66C2] hover:bg-[#084d92] text-white font-medium rounded-md transition-colors"
      >
        <Linkedin size={16} /> Add to LinkedIn profile
      </button>
      <button
        data-testid="certificate-share-linkedin"
        onClick={shareOnLinkedIn}
        className="inline-flex items-center gap-2 px-5 py-3 border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2]/5 font-medium rounded-md transition-colors"
      >
        <Share2 size={16} /> Share as post
      </button>
    </div>
    <p className="mt-4 text-center text-xs text-slate-500">
      Anyone can verify this certificate at{" "}
      <a href={`/certificate/${cert.cert_id}`} target="_blank" rel="noreferrer" className="text-[#E53935] font-medium hover:underline" data-testid="certificate-verify-link">
        {window.location.host}/certificate/{cert.cert_id}
      </a>
    </p>
  </div>
);
